import _ from "lodash";
import * as yup from "yup";

import { FORMATS, parse } from "utils/datetime";

import {
  EFinancingEventType,
  ELoanType,
  EPayableBackTransactionStatus,
  ETaxCreditType,
  ETermType,
  ETransactionStatus,
  ETransactionType,
} from "entities/Deal/constants";

export const FINANCING_EVENT_VALIDATION_SCHEMA = yup.object({
  name: yup
    .string()
    .required("This field is required")
    .max(255, "Must be less than 255 characters"),
  event_type: yup
    .string()
    .oneOf(Object.values(EFinancingEventType))
    .required("This field is required"),
  closing_date: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      if (!_.isNil(originalValue))
        return parse(originalValue, FORMATS.BACKEND.DATE, new Date());
      return null;
    })
    .test(
      "minAndMaxDateValidation",
      "The date must be between 1/1/1900 and 31/12/2099",
      (value) => {
        if (_.isNil(value)) return true; // Allow null or undefined values
        const minDate = new Date(1900, 0, 1); // January 1, 1900
        const maxDate = new Date(2099, 11, 31); // December 31, 2099
        return value >= minDate && value <= maxDate;
      }
    ),
});

export const FINANCING_EVENTS_OPTIONS = Object.values(EFinancingEventType).map(
  (x) => ({
    value: x,
    label: x,
  })
);

export const EMPTY_VALUES = { name: "", event_type: "", closing_date: null };

const TERM_VALIDATION_SCHEMA = yup
  .number()
  .typeError("Value must be a number")
  .nullable()
  .lessThan(999999999999999, "Value must be less than 1000000000000000")
  .positive("Value must be a positive number")
  .integer("Value must be a whole number");

const PERCENTAGE_VALIDATION_SCHEMA = yup
  .number()
  .nullable()
  .typeError("Value must be a number")
  .lessThan(1000, "Value must be less than 1000")
  .positive("Must be a positive number")
  .test(
    "maxDigitsAfterDecimalPoint",
    "Value must have up to 2 digits after the decimal point",
    (value) => {
      if (_.isNil(value)) return true;
      if (value.toString().includes(".")) {
        const strValue = value.toString().split(".");
        return strValue[1].length <= 2;
      }
      return true;
    }
  );

const MONEY_FIELD_VALIDATION_SCHEMA = yup
  .number()
  .typeError("Value must be a number")
  .nullable()
  .lessThan(999999999999999.9999, "Value must be less than 1000000000000000 ")
  .positive("Must be a positive number")
  .test(
    "maxDigitsAfterDecimalPoint",
    "Value must have up to 2 digits after the decimal point",
    (value) => {
      if (_.isNil(value)) return true;
      if (value.toString().includes(".")) {
        const strValue = value.toString().split(".");
        return strValue[1].length <= 2;
      }
      return true;
    }
  );

const AMOUNT_VALIDATION_SCHEMA = yup
  .number()
  .required("This field is required")
  .typeError("Value must be a number")
  .lessThan(999999999999999.9999, "Value must be less than 1000000000000000 ")
  .positive("Must be a positive number")
  .test(
    "maxDigitsAfterDecimalPoint",
    "Value must have up to 2 digits after the decimal point",
    (value) => {
      if (_.isNil(value)) return true;
      if (value.toString().includes(".")) {
        const strValue = value.toString().split(".");
        return strValue[1].length <= 2;
      }
      return true;
    }
  );

const OTHER_SOURCES_VALIDATION_SCHEMA = yup
  .string()
  .max(255, "Must be less than 255 characters")
  .nullable();

const STATUS_VALIDATION_SCHEMA = yup
  .string()
  .nullable()
  .when(
    "transaction_type",
    (transactionType: Array<ETransactionType>, schema) => {
      if (!_.isNil(transactionType[0])) {
        return schema.oneOf(
          TRANSACTION_TYPE_TO_STATUS_MAPPING[transactionType[0]]
        );
      }
      return schema;
    }
  );

const EQUITY_TRANSACATION_VALIDATION_SCHEMA = yup
  .object({
    rate: PERCENTAGE_VALIDATION_SCHEMA,
    term: TERM_VALIDATION_SCHEMA,
    term_type: yup.string().oneOf(Object.values(ETermType)).nullable(),
    housing_credit_equity: MONEY_FIELD_VALIDATION_SCHEMA,
    other_sources: OTHER_SOURCES_VALIDATION_SCHEMA,
    other_sources_amount: MONEY_FIELD_VALIDATION_SCHEMA,
  })
  .nullable();

const DEBT_TRANSACTION_VALIDATION_SCHEMA = yup
  .object({
    rate: PERCENTAGE_VALIDATION_SCHEMA,
    term: TERM_VALIDATION_SCHEMA,
    term_type: yup.string().oneOf(Object.values(ETermType)).nullable(),
    other_sources: OTHER_SOURCES_VALIDATION_SCHEMA,
    other_sources_amount: MONEY_FIELD_VALIDATION_SCHEMA,
    debt_service_coverage_ratio: PERCENTAGE_VALIDATION_SCHEMA,
    number_of_years_extension: yup
      .number()
      .typeError("Value must be a number")
      .positive("Value must be a positive number")
      .integer("Value must be a whole number")
      .lessThan(1000, "Value must be less than 1000")
      .nullable(),
  })
  .nullable();

const TAX_CREDIT_ALLOCATION_TRANSACTION_VALIDATION_SCHEMA = yup
  .object({
    credit_type: yup.string().oneOf(Object.values(ETaxCreditType)).nullable(),
    regulatory_approval_date: yup
      .date()
      .nullable()
      .transform((value, originalValue) => {
        if (!_.isNil(originalValue))
          return parse(originalValue, FORMATS.BACKEND.DATE, new Date());
        return null;
      })
      .test(
        "minAndMaxDateValidation",
        "The date must be between 1/1/1900 and 31/12/2099",
        (value) => {
          if (_.isNil(value)) return true; // Allow null or undefined values
          const minDate = new Date(1900, 0, 1); // January 1, 1900
          const maxDate = new Date(2099, 11, 31); // December 31, 2099
          return value >= minDate && value <= maxDate;
        }
      ),
    first_year_credit_period_per_8609: yup
      .date()
      .nullable()
      .transform((value, originalValue) => {
        if (!_.isNil(originalValue))
          return parse(originalValue, FORMATS.BACKEND.DATE, new Date());
        return null;
      })
      .test(
        "minAndMaxDateValidation",
        "The date must be between 1/1/1900 and 31/12/2099",
        (value) => {
          if (_.isNil(value)) return true; // Allow null or undefined values
          const minDate = new Date(1900, 0, 1); // January 1, 1900
          const maxDate = new Date(2099, 11, 31); // December 31, 2099
          return value >= minDate && value <= maxDate;
        }
      ),
    credit_amount_10_year: MONEY_FIELD_VALIDATION_SCHEMA,
    housing_credit_equity: MONEY_FIELD_VALIDATION_SCHEMA,

    equity_provider: yup
      .string()
      .nullable()
      .max(255, "Must be less than 255 characters"),
    equity_provider_fund_name: yup
      .string()
      .nullable()
      .max(255, "Must be less than 255 characters"),
    equity_amount: MONEY_FIELD_VALIDATION_SCHEMA,
    price_per_credit: MONEY_FIELD_VALIDATION_SCHEMA,
    compliance_period: yup
      .number()
      .typeError("Value must be a number")
      .integer("Value must be a whole number")
      .positive("Value must be a positive number")
      .lessThan(1000, "Value must be less than 1000")
      .nullable(),
    placed_in_service_date: yup
      .date()
      .nullable()
      .transform((value, originalValue) => {
        if (!_.isNil(originalValue))
          return parse(originalValue, FORMATS.BACKEND.DATE, new Date());
        return null;
      })
      .test(
        "minAndMaxDateValidation",
        "The date must be between 1/1/1900 and 31/12/2099",
        (value) => {
          if (_.isNil(value)) return true; // Allow null or undefined values
          const minDate = new Date(1900, 0, 1); // January 1, 1900
          const maxDate = new Date(2099, 11, 31); // December 31, 2099
          return value >= minDate && value <= maxDate;
        }
      ),
  })
  .nullable();

const GRANT_AND_OTHER_TRANSACTION_VALIDATION_SCHEMA = yup
  .object({
    term: TERM_VALIDATION_SCHEMA,
    term_type: yup.string().oneOf(Object.values(ETermType)).nullable(),
    other_sources: OTHER_SOURCES_VALIDATION_SCHEMA,
    other_sources_amount: MONEY_FIELD_VALIDATION_SCHEMA,
  })
  .nullable();

const FINANCING_PROGRAM_ID_VALIDATION_SCHEMA = yup.number().nullable();

const TRANSACTION_TYPE_VALIDATION_SCHEMA = yup
  .string()
  .oneOf(Object.values(ETransactionType))
  .required("This field is required");

const DESCRIPTION_VALIDATION_SCHEMA = yup
  .string()
  .max(2000, "Must be less than 2000 characters")
  .nullable();

const FINANCING_PROGRAM_NAME_VALIDATION_SCHEMA = yup
  .string()
  .nullable()
  .max(255, "Value must be less than 255 characters long");

export const TRANSACTION_VALIDATION_SCHEMA = yup.object({
  financing_program_id: FINANCING_PROGRAM_ID_VALIDATION_SCHEMA,
  financing_program_name: FINANCING_PROGRAM_NAME_VALIDATION_SCHEMA,
  transaction_type: TRANSACTION_TYPE_VALIDATION_SCHEMA,
  description: DESCRIPTION_VALIDATION_SCHEMA,
  status: STATUS_VALIDATION_SCHEMA,
  amount: AMOUNT_VALIDATION_SCHEMA,
  equity_transaction: EQUITY_TRANSACATION_VALIDATION_SCHEMA,
  debt_transaction: DEBT_TRANSACTION_VALIDATION_SCHEMA,
  tax_credit_allocation_transaction:
    TAX_CREDIT_ALLOCATION_TRANSACTION_VALIDATION_SCHEMA,
  grant_transaction: GRANT_AND_OTHER_TRANSACTION_VALIDATION_SCHEMA,
  other_transaction: GRANT_AND_OTHER_TRANSACTION_VALIDATION_SCHEMA,
});

export const TRANSACTION_TYPE_TO_STATUS_MAPPING = {
  [ETransactionType.EQUITY_TRANSACTION]: Object.values(
    EPayableBackTransactionStatus
  ),
  [ETransactionType.DEBT_TRANSACTION]: Object.values(
    EPayableBackTransactionStatus
  ),
  [ETransactionType.TAX_CREDIT_ALLOCATION_TRANSACTION]:
    Object.values(ETransactionStatus),
  [ETransactionType.GRANT_TRANSACTION]: Object.values(ETransactionStatus),
  [ETransactionType.OTHER_TRANSACTION]: Object.values(ETransactionStatus),
};

export const TRANSACTION_EMPTY_VALUES = {
  capital_provider: undefined,
  financing_program_id: null,
  financing_program_name: null,
  transaction_type: "",
  description: null,
  amount: null,
  status: null,
  equity_transaction: null,
  debt_transaction: null,
  tax_credit_allocation_transaction: null,
  grant_transaction: null,
  other_transaction: null,
};

export const EQUITY_TRANSACTION_EMPTY_VALUES = {
  rate: null,
  term: null,
  term_type: null,
  housing_credit_equity: null,
  other_sources: null,
  other_sources_amount: null,
};

export const TAX_CREDIT_ALLOCATION_TRANSACTION_EMPTY_VALUES = {
  credit_type: null,
  regulatory_approval_date: null,
  first_year_credit_period_per_8609: null,
  credit_amount_10_year: null,
  housing_credit_equity: null,
  equity_provider: null,
  equity_provider_fund_name: null,
  equity_amount: null,
  price_per_credit: null,
  compliance_period: null,
  placed_in_service_date: null,
};

export const GRANT_TRANSACTION_EMPTY_VALUES = {
  term: null,
  term_type: null,
  other_sources: null,
  other_sources_amount: null,
};

export const DEBT_TRANSACTION_EMPTY_VALUES = {
  loan_type: null,
  rate: null,
  term: null,
  term_type: null,
  number_of_years_extension: null,
  debt_service_coverage_ratio: null,
  other_sources: null,
  other_sources_amount: null,
};

export const OTHER_TRANSACTION_EMPTY_VALUES = {
  term: null,
  term_type: null,
  other_sources: null,
  other_sources_amount: null,
};

export const calculateTransactionStatusOptions = ({
  transactionType,
}: {
  transactionType: ETransactionType;
}) => {
  switch (transactionType) {
    case ETransactionType.EQUITY_TRANSACTION:
      return Object.values(EPayableBackTransactionStatus).map((x) => ({
        value: x,
        label: x,
      }));
    case ETransactionType.TAX_CREDIT_ALLOCATION_TRANSACTION:
      return Object.values(ETransactionStatus).map((x) => ({
        value: x,
        label: x,
      }));
    case ETransactionType.DEBT_TRANSACTION:
      return Object.values(EPayableBackTransactionStatus).map((x) => ({
        value: x,
        label: x,
      }));
    case ETransactionType.GRANT_TRANSACTION:
      return Object.values(ETransactionStatus).map((x) => ({
        value: x,
        label: x,
      }));
    case ETransactionType.OTHER_TRANSACTION:
      return Object.values(ETransactionStatus).map((x) => ({
        value: x,
        label: x,
      }));
    default:
      return [
        {
          value: "",
          label: "",
        },
      ];
  }
};

export const TRANSACTION_TYPE_OPTIONS = Object.values(ETransactionType).map(
  (x) => ({
    value: x,
    label: x,
  })
);

export const TRANSACTION_EQUITY_EMPTY_VALUES = {};

export const TRANSACTION_TERM_TYPE_OPTIONS = Object.values(ETermType).map(
  (x) => ({
    value: x,
    label: x,
  })
);

export const TRANSACTION_LOAN_TYPE_OPTIONS = Object.values(ELoanType).map(
  (x) => ({
    value: x,
    label: x,
  })
);

export const TRANSACTION_CREDIT_TYPE_OPTIONS = Object.values(
  ETaxCreditType
).map((x) => ({
  value: x,
  label: x,
}));

export enum ESortingType {
  LATEST_FIRST = "Closing date: ascending",
  CHRONOLOGICAL = "Closing date: descending",
}

export enum ETransactionDialogStatus {
  GENERAL_INFO = "General Info",
  SPECIFIC_INFO = "Specific Info",
  CLOSED = "Closed",
}
